import {useMemo} from 'react';

import {
  SemanticLegendByMetric,
  useSemanticLegendsQueryQuery,
} from '../../../../../generated/graphql';
import {useWorkspaceSettingsSemanticLegendPalette} from '../../WorkspaceSettingsContextProvider';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {constructSemanticLegendData} from './constructSemanticLegendData';

export function useSemanticLegendSettings(
  viewId?: string,
  configKeys?: string[]
) {
  const {
    data: semanticLegendSettings,
    error: semanticLegendSettingsError,
    loading: semanticLegendSettingsLoading,
  } = useSemanticLegendsQueryQuery({
    variables: {id: viewId ?? ''},
    skip: viewId === '',
  });

  // Get the aggregates for the current key
  const currentKey =
    semanticLegendSettings?.view?.semanticLegend?.currentMetric; // TODO: change to currentKey

  const semanticLegendsByMetric = useMemo(
    () =>
      semanticLegendSettings?.view?.semanticLegend
        ?.semanticLegendsByMetric as SemanticLegendByMetric[],
    [semanticLegendSettings]
  );
  const semanticLegendPalette = useWorkspaceSettingsSemanticLegendPalette();
  const decodedSemanticLegendData = useMemo(
    () =>
      constructSemanticLegendData(
        semanticLegendPalette,
        currentKey,
        semanticLegendsByMetric
      ),
    [currentKey, semanticLegendsByMetric, semanticLegendPalette]
  );

  const shouldSkipMetricStatistics =
    !currentKey ||
    currentKey === '' ||
    currentKey === DEFAULT_SEMANTIC_LEGEND_SETTINGS.key ||
    (configKeys ?? []).includes(currentKey);

  const shouldSkipConfigValues =
    !currentKey ||
    currentKey === '' ||
    currentKey === DEFAULT_SEMANTIC_LEGEND_SETTINGS.key ||
    !(configKeys ?? []).includes(currentKey);

  return useMemo(
    () => ({
      constructedSemanticLegendData: decodedSemanticLegendData,
      currentKey,
      semanticLegendSettingsError,
      semanticLegendSettingsLoading,
      shouldSkipMetricStatistics,
      shouldSkipConfigValues,
    }),
    [
      currentKey,
      decodedSemanticLegendData,
      semanticLegendSettingsError,
      semanticLegendSettingsLoading,
      shouldSkipMetricStatistics,
      shouldSkipConfigValues,
    ]
  );
}
